import writeXlsxFile from "write-excel-file/browser";
import toast from "react-hot-toast";
import type { DraftCalendarItem } from "@/types/draftCalendar";

export interface ExportRoleOptions {
  isAdmin: boolean;
  isScheduler: boolean;
  canToggleSettings: boolean;
}

type ColumnVisibility = "always" | "admin-scheduler" | "toggle-settings";

interface ExportColumn {
  header: string;
  visibility: ColumnVisibility;
  getValue: (item: DraftCalendarItem) => string | number;
}

function formatExportDate(dateStr: string | null): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${days[d.getDay()]}, ${mm}-${dd}-${d.getFullYear()}`;
}

const EXPORT_COLUMNS: ExportColumn[] = [
  { header: "Draft Meeting Status", visibility: "always", getValue: (i) => i.state ?? "" },
  { header: "Summit Meeting Status", visibility: "admin-scheduler", getValue: (i) => i.summitMeetingStatus ?? "" },
  { header: "DU Status", visibility: "admin-scheduler", getValue: (i) => i.duMeetingStatus ?? "" },
  { header: "Tier", visibility: "toggle-settings", getValue: (i) => i.status ?? "" },
  { header: "Prior ID", visibility: "admin-scheduler", getValue: (i) => i.priorId ?? "" },
  { header: "New ID", visibility: "admin-scheduler", getValue: (i) => i.meetingId ?? "" },
  { header: "Business", visibility: "always", getValue: (i) => i.business ?? "" },
  { header: "Meeting Name", visibility: "always", getValue: (i) => i.meetingName ?? "" },
  { header: "Room Block Start", visibility: "always", getValue: (i) => formatExportDate(i.roomBlockStart) },
  { header: "Room Block End", visibility: "always", getValue: (i) => formatExportDate(i.roomBlockEnd) },
  { header: "Meeting Start", visibility: "always", getValue: (i) => formatExportDate(i.eventStart) },
  { header: "Meeting End", visibility: "always", getValue: (i) => formatExportDate(i.eventEnd) },
  { header: "Start Time", visibility: "always", getValue: (i) => i.startTimeString ?? "" },
  { header: "End Time", visibility: "always", getValue: (i) => i.endTimeString ?? "" },
  { header: "NightGuestNumber", visibility: "always", getValue: (i) => i.nightGuestNumber ?? "" },
  { header: "All In Attendees", visibility: "always", getValue: (i) => i.allInAttendees ?? "" },
  { header: "Meeting Type", visibility: "always", getValue: (i) => i.meetingType ?? "" },
  { header: "Business Line", visibility: "always", getValue: (i) => i.businessLine ?? "" },
  { header: "Attendee Level", visibility: "always", getValue: (i) => i.attendeeLevel ?? "" },
  { header: "Meeting Category", visibility: "always", getValue: (i) => i.meetingCategory ?? "" },
  { header: "CLO or Sponsoring PPMD", visibility: "always", getValue: (i) => i.summitProgramOwnerName ?? "" },
  { header: "Program Contact", visibility: "always", getValue: (i) => i.summitProgramContact ?? "" },
  { header: "Business Advisor", visibility: "always", getValue: (i) => i.summitMeetingPlanner ?? "" },
  { header: "Program Manager", visibility: "always", getValue: (i) => i.programManager ?? "" },
  { header: "Last Updated", visibility: "always", getValue: (i) => String(i.updatedDateFormat ?? "") },
];

function getVisibleColumns(roleOptions: ExportRoleOptions): ExportColumn[] {
  return EXPORT_COLUMNS.filter((col) => {
    switch (col.visibility) {
      case "admin-scheduler":
        return roleOptions.isAdmin || roleOptions.isScheduler;
      case "toggle-settings":
        return roleOptions.canToggleSettings;
      default:
        return true;
    }
  });
}

// All headers regardless of role — use getHeadersForRole() for role-filtered headers
export const ALL_EXPORT_HEADERS = EXPORT_COLUMNS.map((c) => c.header);

export function getHeadersForRole(roleOptions: ExportRoleOptions): string[] {
  return getVisibleColumns(roleOptions).map((c) => c.header);
}

const DEFAULT_ROLE_OPTIONS: ExportRoleOptions = { isAdmin: true, isScheduler: false, canToggleSettings: true };

// Green background RGB(38, 137, 13), white bold text, thin black border
const HEADER_CELL_STYLE = {
  backgroundColor: "#26890D",
  textColor: "#FFFFFF",
  fontWeight: "bold" as const,
  align: "left" as const,
  borderColor: "#000000",
  borderStyle: "thin" as const,
};

export async function exportDraftCalendarToExcel(items: DraftCalendarItem[], roleOptions: ExportRoleOptions = DEFAULT_ROLE_OPTIONS): Promise<void> {
  const columns = getVisibleColumns(roleOptions);
  const headers = columns.map((c) => c.header);

  const headerRow = headers.map((h) => ({ value: h, ...HEADER_CELL_STYLE }));

  const dataRows =
    items.length === 0
      ? [["No record found.", ...Array<string>(headers.length - 1).fill("")]]
      : items.map((item) => columns.map((col) => col.getValue(item)));

  // Auto-size columns
  const columnWidths = headers.map((h) => ({ width: Math.max(h.length + 2, 18) }));

  await writeXlsxFile([headerRow, ...dataRows], { sheet: "Draft Calendar", columns: columnWidths }).toFile("DraftCalendar.xlsx");
  toast.success("Export successful");
}
